import type {MimeType} from "@reva/shared/mime-type";

import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {DownloadedFile} from "../response";
import {WebError} from "../web-error";

/**
 * Common base for every endpoint that answers a GET with a file to download. Subclasses only build
 * the URL; execution and the returned {@link DownloadedFile} (blob + filename from the headers) are
 * handled here.
 */
export abstract class FileDownloadApiInterface extends SimpleApiInterface<DownloadedFile> {
    /**
     * Media type sent as `Accept`. The default leaves it open; override it where the endpoint
     * answers 406 unless the exact type is requested.
     */
    protected acceptMimeType(): MimeType | undefined {
        return undefined;
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<DownloadedFile> {
        return executor.fetchGETFileDownload(url, credentials, this.acceptMimeType());
    }

    /**
     * Passes a {@link WebError} on unchanged instead of mapping it to an {@link ErrorI18n}. The
     * download callers render the error themselves: they read the error code for a specific message
     * ("value.pdf.error.<code>"), fall back to the localized detail the PDF service sends along, and
     * distinguish a 403 (document not licensed) from a real failure. All of that needs the status
     * and the response body, which the generic mapping drops.
     */
    protected handleApiErrors(e: Error): DownloadedFile {
        if (e instanceof WebError) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
